import React, { type MouseEvent, type ReactNode } from 'react';
import { Button, Table, TableProps, TableColumnType, Row, Col } from 'antd';
import { Animation, Icon, Gutter } from '@common/components';
import { Colors } from '@common/tokens';
import { TableFilterControls } from './_table-filter-controls';
import type { FilterFormat } from './_filtered-table-types';
import { clsx } from 'clsx';

const stableScroll = { x: 1280 };

/*
 * Public Row Class Names:
 * 'highlight-recently-updated'
 * 'highlight-action-required'
 **/

export type RenderFilteredTableProps<T extends object> = Omit<
  TableProps<T>,
  'rowClassName'
> & {
  filters: Record<string, unknown>;
  onClearAllFilters: () => void;
  onClearFilter: (
    column: string,
    value?: string | number | boolean | null
  ) => void;
  columns: { filterFormat?: FilterFormat }[];
  rowClassName?: (record: T) => string | undefined;
  headerRightSlot?: ReactNode;
  filtersDataReady?: boolean;
};

// AntD declares this as sending sorter as possibly a list, so
// you will need to guard against a list arg by using instanceof
// Note: list will happen if you send dataIndex prop as list
export type RenderTableOnChange<T> = TableProps<T>['onChange'];

// copy of antd one + custom props
export type ColumnFilterItem = {
  text: ReactNode;
  labelText?: ReactNode;
  value: string | number | boolean;
  children?: ColumnFilterItem[];
};

// Helper type to define non-inline columns.
// Note: if you get cryptic errors/issues defining your columns
// its likely that you've missed or wrongly declared something
export type RenderTableColumn<
  T extends object,
  K extends keyof T = keyof T,
> = Omit<TableColumnType<T>, 'render' | 'dataIndex' | 'filters'> &
  NonNullable<
    | {
        [prop in K]: {
          filterFormat?: FilterFormat;
          filters?: ColumnFilterItem[];
          dataIndex: prop;
          render?: (value: T[prop], record: T) => ReactNode;
        };
      }[K]
    | {
        filterFormat?: FilterFormat;
        filters?: ColumnFilterItem[];
        dataIndex?: undefined | (number | string)[];
        render?: (value: unknown, record: T) => ReactNode;
      }
  >;

export const RenderFilteredTable = <T extends object>({
  columns,
  scroll,
  filters,
  onClearAllFilters,
  onClearFilter,
  rowClassName,
  onRow,
  headerRightSlot,
  filtersDataReady = true,
  expandable,
  rowKey = 'key',
  ...props
}: RenderFilteredTableProps<T>) => (
  // do not put any state or logic here, this component is render only.
  <section className="FilteredTable">
    <Row justify="space-between" style={{ width: '100%', flexWrap: 'nowrap' }}>
      <Col flex="1 1 auto">
        {filtersDataReady && (
          <TableFilterControls
            filters={filters}
            clearAllFilters={onClearAllFilters}
            clearFilter={onClearFilter}
            columns={columns}
          />
        )}
      </Col>
      <Col style={{ minWidth: 'fit-content' }}>
        {headerRightSlot && (
          <div className="header-right-slot">{headerRightSlot}</div>
        )}
      </Col>
    </Row>
    <Table
      {...props}
      rowKey={rowKey}
      expandable={
        expandable
          ? {
              ...expandable,
              expandIcon: expandable.expandIcon ?? DefaultExpandIcon,
            }
          : undefined
      }
      onRow={onRow}
      columns={columns}
      scroll={scroll ?? stableScroll}
      rowClassName={(record) => {
        const isRowClickable = !!onRow?.(record)?.onClick;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: preserving js version behavior
        const recordClassName: string = record.rowClassName;
        const effectiveClassName = recordClassName || rowClassName?.(record);

        return clsx(
          {
            clickable: isRowClickable,
            'data-row': true,
          },
          effectiveClassName
        );
      }}
    />
    <style jsx>
      {`
        .FilteredTable {
          margin-top: 12px;
        }
        .FilteredTable :global(.ant-table-tbody > tr.disabled-row:hover > td) {
          background: ${Colors.steelLightest};
        }
        .FilteredTable :global(.disabled-row td) {
          background: ${Colors.steelLightest};
        }
        // FIXME: this global affects everywhere outside of this component
        //  and shouldn't be here.
        :global(.ant-table-filter-dropdown .ant-dropdown-menu-item-selected) {
          background-color: white;
        }
        // FIXME: this global affects everywhere outside of this component
        //  and shouldn't be here.
        :global(.ant-dropdown-menu-item) {
          display: flex;
        }
        .FilteredTable :global(tr.highlight-action-required > td:first-child) {
          border-left: solid 4px ${Colors.elm};
        }
        .FilteredTable :global(tr.highlight-action-required) {
          font-weight: bold;
        }
        .FilteredTable :global(.clickable) {
          cursor: pointer;
        }
        .FilteredTable
          :global(
            tr.highlight-recently-updated .ant-table-cell-fix-left,
            tr.highlight-recently-updated .ant-table-cell-fix-right,
            tr.highlight-recently-updated
          ) {
          background: ${Colors.elmHyperlight};
        }

        .FilteredTable :global(.ant-table-cell-fix-right) {
          z-index: 1;
        }

        .FilteredTable .header-right-slot {
          margin-bottom: 12px;
        }
        .FilteredTable :global(.filtered-table-expand-icon .ant-btn) {
          padding: 0 8px 0 0;
          height: 16px;
        }
        .FilteredTable :global(.filtered-table-expand-icon .wk-animation) {
          line-height: 0;
          vertical-align: top;
        }
      `}
    </style>
  </section>
);

function DefaultExpandIcon<T extends object>({
  expanded,
  expandable: isExpandable,
  record,
  onExpand,
}: {
  expanded: boolean;
  expandable: boolean;
  record: T;
  onExpand: (record: T, e: MouseEvent<HTMLElement>) => void;
}) {
  if (!isExpandable) return <Gutter size="md" direction="left" />;

  return (
    <span className="filtered-table-expand-icon">
      <Button
        type="link"
        onClick={(e) => {
          e.stopPropagation();
          onExpand(record, e);
        }}
      >
        <Animation kind="rotate-left" active={expanded}>
          <Icon icon="chevronDown" />
        </Animation>
      </Button>
    </span>
  );
}
